import React, { useState, useEffect } from 'react';
import "../../css/Parents.css";
import Loader from "../../components/Loader";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaSearch, FaUserFriends, FaEdit, FaTrash, FaFilter, FaTimes } from 'react-icons/fa';

function Parents() {
    const [parents, setParents] = useState([]);
    const [filteredParents, setFilteredParents] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterOption, setFilterOption] = useState('all');
    const [showFilters, setShowFilters] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [currentParent, setCurrentParent] = useState(null);
    const [editFormData, setEditFormData] = useState({});

    useEffect(() => {
        fetchParents();
    }, []);

    const fetchParents = async () => {
        const token = localStorage.getItem("token");
        if (!token) {
            toast.error("Unauthorized! Please log in again.");
            return;
        }

        setIsLoading(true);

        try {
            const response = await fetch("https://schoolpro-api.uniums.com/api/parents", {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to fetch parents");
            }

            const data = await response.json();
            setParents(data);
            setFilteredParents(data);
        } catch (error) {
            console.error("Error fetching parents:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle opening the edit modal
    const handleEditClick = (parent) => {
        setCurrentParent(parent);
        setEditFormData({ ...parent });
        setShowEditModal(true);
    };

    // Handle opening the delete modal
    const handleDeleteClick = (parent) => {
        setCurrentParent(parent);
        setShowDeleteModal(true);
    };

    // Handle form input changes
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setEditFormData({
            ...editFormData,
            [name]: value
        });
    };

    // Handle form submission for update
    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/parents/${currentParent._id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(editFormData)
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to update parent");
            }

            const data = await response.json();

            const updatedParents = parents.map(parent =>
                parent._id === currentParent._id ? data.parent : parent
            );

            setParents(updatedParents);
            setFilteredParents(updatedParents);

            toast.success("Parent updated successfully!");
            setShowEditModal(false);
        } catch (error) {
            console.error("Error updating parent:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle parent deletion
    const handleDeleteConfirm = async () => {
        setIsLoading(true);

        const token = localStorage.getItem("token");

        try {
            const response = await fetch(`https://schoolpro-api.uniums.com/api/parents/${currentParent._id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Failed to delete parent");
            }

            const updatedParents = parents.filter(parent => parent._id !== currentParent._id);
            setParents(updatedParents);
            setFilteredParents(updatedParents);

            toast.success("Parent deleted successfully!");
            setShowDeleteModal(false);
        } catch (error) {
            console.error("Error deleting parent:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    // Handle search and filtering
    useEffect(() => {
        let results = parents;

        if (searchTerm) {
            results = results.filter(parent =>
                (parent.firstName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.lastName?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.userCode?.toString() || "").includes(searchTerm) ||
                (parent.relationship?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
                (parent.email?.toString().toLowerCase() || "").includes(searchTerm.toLowerCase())
            );
        }

        if (filterOption !== 'all') {
            results = results.filter(parent =>
                parent.relationship?.toString().toLowerCase() === filterOption.toLowerCase()
            );
        }

        setFilteredParents(results);
    }, [searchTerm, filterOption, parents]);

    // Get unique relationships for filter dropdown
    const relationships = [...new Set(parents.map(parent => parent.relationship?.toString()))]
        .filter(Boolean)
        .sort();

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleFilterChange = (e) => {
        setFilterOption(e.target.value);
    };

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    // Format date safely
    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    return (
        <div className="parents-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />

            {isLoading && <Loader />}

            <div className="parents-header">
                <div className="parents-title-container">
                    <FaUserFriends className="parents-icon" />
                    <h1 className="parents-title">Parent Directory</h1>
                </div>

                <div className="parents-controls">
                    <div className="parents-search-container">
                        <FaSearch className="parents-search-icon" />
                        <input
                            type="text"
                            className="parents-search-input"
                            placeholder="Search by name, user code, relationship..."
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                    </div>

                    <button className="parents-filter-toggle-btn" onClick={toggleFilters}>
                        <FaFilter /> Filters
                    </button>
                </div>

                {showFilters && (
                    <div className="parents-filter-options">
                        <select
                            value={filterOption}
                            onChange={handleFilterChange}
                            className="parents-filter-select"
                        >
                            <option value="all">All Parents</option>
                            {relationships.map(relationship => (
                                <option key={relationship} value={relationship}>{relationship}</option>
                            ))}
                        </select>
                    </div>
                )}

                <div className="parents-stats">
                    <div className="parents-stat-box">
                        <span className="parents-stat-number">{filteredParents.length}</span>
                        <span className="parents-stat-label">Total Parents</span>
                    </div>
                    {filterOption !== 'all' && (
                        <div className="parents-stat-box highlight">
                            <span className="parents-stat-number">{filterOption}</span>
                            <span className="parents-stat-label">Relationship</span>
                        </div>
                    )}
                </div>
            </div>

            {filteredParents.length === 0 && !isLoading ? (
                <div className="parents-empty">
                    <div className="parents-empty-icon">
                        <FaSearch size={48} color="#a0aec0" />
                    </div>
                    <p className="parents-empty-text">No parents found matching your criteria.</p>
                    {searchTerm && (
                        <button className="parents-reset-search" onClick={() => setSearchTerm('')}>
                            Clear Search
                        </button>
                    )}
                </div>
            ) : (
                <div className="parents-grid">
                    {filteredParents.map((parent, index) => {
                        const firstInitial = parent.firstName ? parent.firstName.charAt(0) : '';
                        const lastInitial = parent.lastName ? parent.lastName.charAt(0) : '';
                        const relationship = parent.relationship?.toString().toLowerCase() || 'other';

                        return (
                            <div key={index} className="parents-card">
                                <div className={`parents-card-header ${relationship === 'mother' ? 'female' : 'male'}`}>
                                    <div className="parents-avatar">
                                        {firstInitial}{lastInitial}
                                    </div>
                                    <div className="parents-header-info">
                                        <h2 className="parents-name">
                                            {parent.firstName || ''} {parent.lastName || ''}
                                        </h2>
                                        <span className="parents-position">
                                            {parent.relationship || 'Parent'}
                                        </span>
                                    </div>
                                </div>

                                <div className="parents-card-body">
                                    <div className="parents-quick-info">
                                        <span className="parents-relationship-badge">{parent.relationship || 'N/A'}</span>
                                        <span className="parents-id-badge">Code: {parent.userCode || 'N/A'}</span>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Parent Info</h3>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Student:</span>
                                            <span className="parents-info-value">{parent.student?.firstName} {parent.student?.lastName || 'N/A'}</span>
                                        </div>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Occupation:</span>
                                            <span className="parents-info-value">{parent.occupation || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Contact Info</h3>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Email:</span>
                                            <span className="parents-info-value">{parent.email || 'N/A'}</span>
                                        </div>
                                        <div className="parents-info-group">
                                            <span className="parents-info-label">Phone:</span>
                                            <span className="parents-info-value">{parent.phone || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-section">
                                        <h3 className="parents-section-title">Address</h3>
                                        <div className="parents-info-group parents-address">
                                            <span className="parents-info-value">{parent.address || 'N/A'}</span>
                                        </div>
                                    </div>

                                    <div className="parents-info-group parents-created-date">
                                        <span className="parents-info-label">Added:</span>
                                        <span className="parents-info-value">{formatDate(parent.createdAt)}</span>
                                    </div>
                                </div>

                                <div className="parents-card-footer">
                                    <button className="parents-action-btn parents-edit-btn" onClick={() => handleEditClick(parent)}>
                                        <FaEdit /> Edit
                                    </button>
                                    <button className="parents-action-btn parents-delete-btn" onClick={() => handleDeleteClick(parent)}>
                                        <FaTrash /> Delete
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Edit Modal */}
            {showEditModal && (
                <div className="parents-modal-overlay">
                    <div className="parents-modal-container">
                        <div className="parents-modal-header">
                            <h2>Edit Parent</h2>
                            <button className="parents-close-btn" onClick={() => setShowEditModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="parents-modal-body">
                            <form onSubmit={handleUpdateSubmit}>
                                <div className="parents-form-row">
                                    <div className="parents-form-group half">
                                        <label>First Name:</label>
                                        <input
                                            type="text"
                                            name="firstName"
                                            value={editFormData.firstName || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="parents-form-group half">
                                        <label>Last Name:</label>
                                        <input
                                            type="text"
                                            name="lastName"
                                            value={editFormData.lastName || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="parents-form-row">
                                    <div className="parents-form-group half">
                                        <label>Email:</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={editFormData.email || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="parents-form-group half">
                                        <label>Phone:</label>
                                        <input
                                            type="text"
                                            name="phone"
                                            value={editFormData.phone || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="parents-form-row">
                                    <div className="parents-form-group half">
                                        <label>User Code:</label>
                                        <input
                                            type="text"
                                            name="userCode"
                                            value={editFormData.userCode || ''}
                                            onChange={handleInputChange}
                                            required
                                        />
                                    </div>
                                    <div className="parents-form-group half">
                                        <label>Relationship:</label>
                                        <select
                                            name="relationship"
                                            value={editFormData.relationship || ''}
                                            onChange={handleInputChange}
                                            required
                                        >
                                            <option value="">Select Relationship</option>
                                            <option value="father">Father</option>
                                            <option value="mother">Mother</option>
                                            <option value="guardian">Guardian</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                </div>

                                <div className="parents-form-group">
                                    <label>Occupation:</label>
                                    <input
                                        type="text"
                                        name="occupation"
                                        value={editFormData.occupation || ''}
                                        onChange={handleInputChange}
                                    />
                                </div>

                                <div className="parents-form-group">
                                    <label>Address:</label>
                                    <textarea
                                        name="address"
                                        value={editFormData.address || ''}
                                        onChange={handleInputChange}
                                        required
                                    ></textarea>
                                </div>

                                <div className="parents-modal-footer">
                                    <button type="button" className="parents-cancel-btn" onClick={() => setShowEditModal(false)}>
                                        Cancel
                                    </button>
                                    <button type="submit" className="parents-save-btn">
                                        Update Parent
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="parents-modal-overlay">
                    <div className="parents-modal-container parents-delete-modal">
                        <div className="parents-modal-header">
                            <h2>Delete Parent</h2>
                            <button className="parents-close-btn" onClick={() => setShowDeleteModal(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="parents-modal-body">
                            <div className="parents-delete-warning">
                                <FaTrash className="parents-delete-icon" />
                                <p>Are you sure you want to delete {currentParent?.firstName} {currentParent?.lastName}?</p>
                                <p className="parents-warning-text">This action cannot be undone.</p>
                            </div>
                            <div className="parents-modal-footer">
                                <button className="parents-cancel-btn" onClick={() => setShowDeleteModal(false)}>
                                    Cancel
                                </button>
                                <button className="parents-delete-confirm-btn" onClick={handleDeleteConfirm}>
                                    Delete Parent
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Parents;